import { trackTransition } from '@/components/SwitchTrackModal/trackTransition';
import { announcePatientTrackChange } from '@/hooks/usePatientTrack';
import { reportError } from '@/services/error-reporting';
import { fetchBot } from '@/services/services';
import { recordTrackSwitchProvenance } from '@/services/trackSwitchProvenance';
import { Track } from '@bariendo/lib/constants';
import { createReference, MedplumClient } from '@medplum/core';
import { Patient, Practitioner } from '@medplum/fhirtypes';
import type { SwitchPatientTrackEvent } from '../../../../../../bots/src/handlers/switch-patient-track';

/**
 * Moves the patient to `to` through `switch-patient-track`, the same bot and
 * acknowledgement rule as the Switch track dialog (`trackTransition`), records
 * who did it, and has every reader of the track on the page re-read it. Shared
 * by the coverage buttons (BAR-2921) and the PN card v2.0's save (BAR-2995).
 *
 * Throws when the bot refuses or fails, or when the move needs the
 * acknowledgement and it wasn't given. The page re-reads the track either way.
 */
export async function switchPatientTrack(
  medplum: MedplumClient,
  patient: Patient,
  profile: Practitioner | undefined,
  from: Track | undefined,
  to: Track,
  acknowledged: boolean,
  component: string
): Promise<void> {
  const acknowledgementNeeded = trackTransition(from, to).needsAcknowledgement;
  if (acknowledgementNeeded && !acknowledged) throw new Error('Acknowledge the track change first');
  const input: SwitchPatientTrackEvent = {
    patient: createReference(patient),
    toTrack: to,
    acknowledgeRetroactivePhiExposure: acknowledgementNeeded ? true : undefined,
  };
  try {
    await fetchBot(medplum, 'switch-patient-track', input);
  } catch (error) {
    // Re-read before the next try: the patient may already be on the target (moved from another tab).
    if (patient.id) announcePatientTrackChange(patient.id);
    throw error;
  }
  if (profile) {
    // As in the Switch track dialog: the track has moved, and losing the
    // attribution is a smaller failure than reporting the switch as failed.
    await recordTrackSwitchProvenance(medplum, patient, profile, from, to).catch((error) =>
      reportError(error, { component, extra: { reason: 'provenance-write-failed' } })
    );
  }
  if (patient.id) announcePatientTrackChange(patient.id);
}
